import { Typography } from "@mui/material"
import { AddOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useState } from "react"

import type { InventoryDestinationFormValues } from "../../../../schemas"
import type { TransferFormPallet, TransferFormProductInventory } from "../../types"
import { calculateTransferFormTotals } from "../../utils/transfer"
import { TransferPalletDestinationOverrideDialog } from "../TransferPalletDestinationOverrideDialog"
import { InterTransferPalletSelectionDialog } from "../TransferPalletSelectionDialog/InterTransferPalletSelectionDialog"
import { IntraTransferPalletSelectionDialog } from "../TransferPalletSelectionDialog/IntraTransferPalletSelectionDialog"
import { TransferProductSelectionDialog } from "../TransferProductSelectionDialog/TransferProductSelectionDialog"
import { TransferTotalsSummary } from "../TransferTotalsSummary"

import { TransferFormProductTable } from "./TransferFormProductTable"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { Button } from "@/components/common/Button"
import ConfirmationDialog from "@/components/common/Modals/ConfirmationDialog"
import type { InventoryByProductItem, WarehouseArea } from "@/graphql/codegen/graphql"

enum TransferProductActionType {
  SelectProducts = "SELECT_PRODUCTS",
  RemoveProduct = "REMOVE_PRODUCT",
  SelectPallets = "SELECT_PALLETS",
  OverridePalletLocation = "OVERRIDE_PALLET_LOCATION",
  RemovePallet = "REMOVE_PALLET",
  RemoveLot = "REMOVE_LOT",
}

type TransferProductAction =
  | { type: TransferProductActionType.SelectProducts }
  | { type: TransferProductActionType.RemoveProduct; productId: number }
  | { type: TransferProductActionType.SelectPallets; productId: number }
  | { type: TransferProductActionType.OverridePalletLocation; productId: number; palletReferenceNumber: number }
  | { type: TransferProductActionType.RemovePallet; productId: number; palletReferenceNumber: number }
  | { type: TransferProductActionType.RemoveLot; productId: number; rawInventoryId: string }

interface TransferFormProductsProps {
  sourceWarehouseArea: WarehouseArea
  targetWarehouseArea?: WarehouseArea
  inventories: TransferFormProductInventory[]
  readOnly?: boolean
  onProductsAdd: (inventories: InventoryByProductItem[]) => void
  onProductRemove: (productId: number) => void
  onPalletsChange: (productId: number, pallets: TransferFormPallet[]) => void
}

export const TransferFormProducts: FC<TransferFormProductsProps> = ({
  sourceWarehouseArea,
  targetWarehouseArea,
  inventories,
  readOnly = false,
  onProductsAdd,
  onProductRemove,
  onPalletsChange,
}) => {
  const [activeAction, setActiveAction] = useState<TransferProductAction | null>(null)

  const totals = calculateTransferFormTotals(inventories)

  const resetAction = () => setActiveAction(null)

  const handleActionClose = () => resetAction()

  const handleProductsSubmit = (inventories: InventoryByProductItem[]) => {
    onProductsAdd(inventories)
    resetAction()
  }

  const handleProductRemoveConfirm = () => {
    if (activeAction?.type !== TransferProductActionType.RemoveProduct) return

    onProductRemove(activeAction.productId)
    resetAction()
  }

  const handlePalletsSubmit = (pallets: TransferFormPallet[]) => {
    if (activeAction?.type !== TransferProductActionType.SelectPallets) return

    onPalletsChange(activeAction.productId, pallets)
    resetAction()
  }

  const handlePalletOverrideConfirm = (destination: InventoryDestinationFormValues) => {
    if (activeAction?.type !== TransferProductActionType.OverridePalletLocation) return

    const targetInventory = inventories.find((inventory) => inventory.product.productId === activeAction.productId)

    if (!targetInventory) return

    const pallets = targetInventory.pallets.map((pallet) =>
      pallet.referenceNbr === activeAction.palletReferenceNumber ? { ...pallet, destination } : pallet
    )
    onPalletsChange(activeAction.productId, pallets)
    resetAction()
  }

  const handlePalletRemoveConfirm = () => {
    if (activeAction?.type !== TransferProductActionType.RemovePallet) return

    const targetInventory = inventories.find((inventory) => inventory.product.productId === activeAction.productId)

    if (!targetInventory) return

    const pallets = targetInventory.pallets.filter(
      (pallet) => pallet.referenceNbr !== activeAction.palletReferenceNumber
    )
    onPalletsChange(activeAction.productId, pallets)
    resetAction()
  }

  const handleLotRemoveConfirm = () => {
    if (activeAction?.type !== TransferProductActionType.RemoveLot) return

    const targetInventory = inventories.find((inventory) => inventory.product.productId === activeAction.productId)

    if (!targetInventory) return

    const pallets = targetInventory.pallets
      .map((pallet) => ({
        ...pallet,
        lots: pallet.lots.filter((lot) => lot.rawInventoryId !== activeAction.rawInventoryId),
      }))
      .filter((pallet) => pallet.lots.length)
    onPalletsChange(activeAction.productId, pallets)
    resetAction()
  }

  return (
    <div className='flex h-full flex-col gap-y-6'>
      {inventories.length ? (
        <>
          <div className='flex items-center justify-between'>
            <Typography variant='h6' className='font-bold leading-10 text-gray-800'>
              Products to Transfer
            </Typography>
            <Button
              appearance='contained'
              startIcon={<AddOutlined />}
              disabled={readOnly}
              onClick={() => setActiveAction({ type: TransferProductActionType.SelectProducts })}
              data-testid='transfer.form-page.add-products-button'
            >
              Add Products
            </Button>
          </div>
          <div className='flex min-h-0 flex-1 overflow-y-auto'>
            <TransferFormProductTable
              data={inventories}
              targetWarehouseArea={targetWarehouseArea}
              readOnly={readOnly}
              onProductRemove={(productId) =>
                setActiveAction({ type: TransferProductActionType.RemoveProduct, productId })
              }
              onPalletsSelect={(productId) =>
                setActiveAction({ type: TransferProductActionType.SelectPallets, productId })
              }
              onPalletOverride={(productId, palletReferenceNumber) =>
                setActiveAction({
                  type: TransferProductActionType.OverridePalletLocation,
                  productId,
                  palletReferenceNumber,
                })
              }
              onPalletRemove={(productId, palletReferenceNumber) =>
                setActiveAction({ type: TransferProductActionType.RemovePallet, productId, palletReferenceNumber })
              }
              onLotRemove={(productId, rawInventoryId) =>
                setActiveAction({ type: TransferProductActionType.RemoveLot, productId, rawInventoryId })
              }
            />
          </div>
          <TransferTotalsSummary totals={totals} />
        </>
      ) : (
        <InformationMissingAlert
          title='No Products added yet. Add a product to get started.'
          action={
            <Button
              appearance='outlined'
              size='small'
              startIcon={<AddOutlined />}
              disabled={readOnly}
              onClick={() => setActiveAction({ type: TransferProductActionType.SelectProducts })}
              data-testid='transfer.form-page.add-products-button'
            >
              Add Products
            </Button>
          }
        />
      )}
      {activeAction?.type === TransferProductActionType.SelectProducts && (
        <TransferProductSelectionDialog
          warehouseAreaId={sourceWarehouseArea.warehouseAreaId}
          addedInventories={inventories}
          onClose={handleActionClose}
          onSubmit={handleProductsSubmit}
        />
      )}
      {activeAction?.type === TransferProductActionType.SelectPallets && targetWarehouseArea && (
        <InterTransferPalletSelectionDialog
          inventories={inventories}
          productId={activeAction.productId}
          warehouseArea={sourceWarehouseArea}
          targetWarehouseArea={targetWarehouseArea}
          onClose={handleActionClose}
          onSubmit={handlePalletsSubmit}
        />
      )}
      {activeAction?.type === TransferProductActionType.SelectPallets && !targetWarehouseArea && (
        <IntraTransferPalletSelectionDialog
          inventories={inventories}
          productId={activeAction.productId}
          warehouseArea={sourceWarehouseArea}
          onClose={handleActionClose}
          onSubmit={handlePalletsSubmit}
        />
      )}
      {activeAction?.type === TransferProductActionType.OverridePalletLocation && (
        <TransferPalletDestinationOverrideDialog
          inventories={inventories}
          productId={activeAction.productId}
          palletReferenceNumber={activeAction.palletReferenceNumber}
          warehouseArea={sourceWarehouseArea}
          onClose={handleActionClose}
          onConfirm={handlePalletOverrideConfirm}
        />
      )}
      {activeAction?.type === TransferProductActionType.RemoveProduct && (
        <ConfirmationDialog
          title='Remove Product'
          content='Are you sure you want to remove this product? This product will be removed from the transfer list.'
          closeLabel='Cancel'
          confirmLabel='Remove Product'
          onClose={handleActionClose}
          onConfirm={handleProductRemoveConfirm}
        />
      )}
      {activeAction?.type === TransferProductActionType.RemovePallet && (
        <ConfirmationDialog
          title='Remove Pallet'
          content='Are you sure you want to remove this pallet from the product?'
          closeLabel='Cancel'
          confirmLabel='Remove Pallet'
          onClose={handleActionClose}
          onConfirm={handlePalletRemoveConfirm}
        />
      )}
      {activeAction?.type === TransferProductActionType.RemoveLot && (
        <ConfirmationDialog
          title='Remove Lot'
          content='Are you sure you want to remove this lot from the product?'
          closeLabel='Cancel'
          confirmLabel='Remove Lot'
          onClose={handleActionClose}
          onConfirm={handleLotRemoveConfirm}
        />
      )}
    </div>
  )
}
